export interface ZoomState {
  zoom: number;
  panX: number;
  panY: number;
}

type Listener = (state: ZoomState) => void;

const MIN_ZOOM = 0.05;
const MAX_ZOOM = 32;

function clampZoom(zoom: number): number {
  if (zoom < MIN_ZOOM) return MIN_ZOOM;
  if (zoom > MAX_ZOOM) return MAX_ZOOM;
  return zoom;
}

export class ZoomStore {
  private state: ZoomState = { zoom: 1, panX: 0, panY: 0 };
  private listeners: Listener[] = [];

  get(): ZoomState {
    return this.state;
  }

  set(patch: Partial<ZoomState>): void {
    this.state = { ...this.state, ...patch, zoom: clampZoom(patch.zoom ?? this.state.zoom) };
    for (const listener of this.listeners) listener(this.state);
  }

  subscribe(listener: Listener): void {
    this.listeners.push(listener);
  }

  fit(canvasSize: { w: number; h: number }, viewportSize: { w: number; h: number }): void {
    if (
      canvasSize.w <= 0 ||
      canvasSize.h <= 0 ||
      viewportSize.w <= 0 ||
      viewportSize.h <= 0
    ) {
      this.set({ zoom: 1, panX: 0, panY: 0 });
      return;
    }

    const zoom =
      Math.min(viewportSize.w / canvasSize.w, viewportSize.h / canvasSize.h) * 0.95;
    this.set({
      zoom,
      panX: (viewportSize.w - canvasSize.w * zoom) / 2,
      panY: (viewportSize.h - canvasSize.h * zoom) / 2,
    });
  }

  zoomToward(anchor: { x: number; y: number }, factor: number): void {
    const { zoom, panX, panY } = this.state;
    const targetZoom = clampZoom(zoom * factor);
    const worldX = (anchor.x - panX) / zoom;
    const worldY = (anchor.y - panY) / zoom;
    this.set({
      zoom: targetZoom,
      panX: anchor.x - worldX * targetZoom,
      panY: anchor.y - worldY * targetZoom,
    });
  }

  pan(dx: number, dy: number): void {
    this.set({
      panX: this.state.panX + dx,
      panY: this.state.panY + dy,
    });
  }
}
